import type { Rect } from '../core/types';
import type { Framebuffer } from '../gl/framebuffer';
import type { RenderTarget } from '../gl/renderTarget';
import type { Texture } from '../gl/texture';
import {
  channelPixelOp,
  maskPixelOp,
  pixelOp,
  type EngineHistoryOp,
} from './historyOps';
import type { DocumentResources } from './resources';

export type StrokeTargetKind = 'surface' | 'mask' | 'channel';

/**
 * Where a stroke lands. Photopea allows every painting tool onto a layer, a
 * raster mask or a channel, and the only differences are the number of channels
 * and which history op records the change — so tools go through this instead of
 * reaching for a layer surface directly.
 */
export interface StrokeTarget {
  readonly kind: StrokeTargetKind;
  /** Layer id for a surface or mask target, channel id for a channel target. */
  readonly id: string;
  /** Current content, read by the preview pass. */
  readonly texture: Texture;
  /** Destination the finished stroke is copied into. */
  readonly framebuffer: Framebuffer;
  /** Scratch target that holds the live preview of the stroke. */
  readonly preview: RenderTarget;
  /**
   * Colour channel a surface stroke is confined to, or null for all of them.
   * Painting one colour channel is the same stroke with the other channels
   * masked out of the write.
   */
  readonly colorChannel: number | null;
  readPixels(rect: Rect): Uint8Array;
  historyOp(rect: Rect, before: Uint8Array, after: Uint8Array): EngineHistoryOp;
}

/** Scratch slots. Only one stroke is ever live, so the three kinds can share. */
const PREVIEW_RGBA_SLOT = 0;
const PREVIEW_R8_SLOT = 3;

export function surfaceStrokeTarget(
  resources: DocumentResources,
  layerId: string,
  colorChannel: number | null = null,
): StrokeTarget {
  const surface = resources.ensureLayer(layerId).surface;
  return {
    kind: 'surface',
    id: layerId,
    texture: surface.texture,
    framebuffer: surface.target.framebuffer,
    preview: resources.rgba(PREVIEW_RGBA_SLOT),
    colorChannel,
    readPixels: (rect) => surface.readPixels(rect),
    historyOp: (rect, before, after) =>
      pixelOp(layerId, { rect, data: before }, { rect, data: after }),
  };
}

export function maskStrokeTarget(resources: DocumentResources, layerId: string): StrokeTarget {
  const mask = resources.ensureMask(layerId);
  return {
    kind: 'mask',
    id: layerId,
    texture: mask.texture,
    framebuffer: mask.framebuffer,
    preview: resources.r8(PREVIEW_R8_SLOT),
    colorChannel: null,
    readPixels: (rect) => mask.texture.readPixels(rect),
    historyOp: (rect, before, after) =>
      maskPixelOp(layerId, { rect, data: before }, { rect, data: after }),
  };
}

export function channelStrokeTarget(
  resources: DocumentResources,
  channelId: string,
): StrokeTarget {
  const channel = resources.ensureChannel(channelId);
  return {
    kind: 'channel',
    id: channelId,
    texture: channel.texture,
    framebuffer: channel.framebuffer,
    preview: resources.r8(PREVIEW_R8_SLOT),
    colorChannel: null,
    readPixels: (rect) => channel.texture.readPixels(rect),
    historyOp: (rect, before, after) =>
      channelPixelOp(channelId, { rect, data: before }, { rect, data: after }),
  };
}
